import { Controller } from "@hotwired/stimulus"
import { showNotification, isoDateToString, localDateToIso } from "../helpers"

export default class extends Controller {
  static targets = ["mfrDate", "searchKind", "warnAlert", "addPositionButton"]
  static outlets = ["invoice", "invoice--position"]
  static values = {
    sumMscUrl: String
  }

  invoiceOutletConnected() {
    this.tryInitialize()
  }

  invoicePositionOutletConnected(outlet) {
    outlet.element.addEventListener("invoice--position:added", this.onPositionAdded = () => {
      if (!this.autoFilling) this.userAddedPosition = true
    })
  }

  disconnect() {
    this.fetchController?.abort()
    this.invoicePositionOutlet?.element.removeEventListener("invoice--position:added", this.onPositionAdded)
  }

  tryInitialize() {
    if (this.initialized || !this.hasInvoiceOutlet) return

    this.initialized = true

    this.applyMfrDate()
    this.checkWarn()
    if (this.invoiceOutlet.newRecordValue) this.fetchAndFillPositions({ respectExistingPositions: true })
  }

  refresh() {
    this.applyMfrDate()
    this.checkWarn()
    if (this.invoiceOutlet.newRecordValue) this.fetchAndFillPositions()
  }

  applyMfrDate() {
    const computed = this.findMfrDate()
    if (!computed || !this.invoiceOutlet.hasSellDateTarget) return

    this.invoiceOutlet.sellDateTarget.value = isoDateToString(computed)
    this.invoiceOutlet.sellDateTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  findMfrDate() {
    if (!this.hasMfrDateTarget) return null

    const [year, month] = (this.mfrDateTarget.value || "").split("-").map(Number)
    if (!year || !month) return null

    const today = new Date()
    const firstDayOfMonth = new Date(year, month - 1, 1)
    if (today < firstDayOfMonth) return null // przyszły miesiąc — nie ma jeszcze czego raportować

    const lastDayOfMonth = new Date(year, month, 0)
    return today < lastDayOfMonth ? today : lastDayOfMonth
  }

  // Pola dat trzymają wartość w formacie locale'u konta (dd.mm.yyyy) albo ISO, a oba warianty
  // muszą wyjść jako północ czasu lokalnego: `new Date("2026-09-25")` daje północ UTC, więc przy
  // dodatnim offsecie strefy data wypadała PO granicy okna liczonej konstruktorem lokalnym.
  parseFieldDate(value) {
    const [year, month, day] = localDateToIso((value || "").trim()).split("-").map(Number)
    if (!year || !month || !day) return null

    return new Date(year, month - 1, day)
  }

  checkWarn() {
    if (!this.hasWarnAlertTarget || !this.invoiceOutlet.hasSellDateTarget || !this.invoiceOutlet.hasIssueDateTarget) return

    const sellDate = this.parseFieldDate(this.invoiceOutlet.sellDateTarget.value)
    const issueDate = this.parseFieldDate(this.invoiceOutlet.issueDateTarget.value)
    if (!sellDate || !issueDate) return

    const minRange = new Date(sellDate.getFullYear(), sellDate.getMonth(), 26)
    const maxRange = new Date(sellDate.getFullYear(), sellDate.getMonth() + 1, 25)
    const inRange = minRange <= issueDate && issueDate <= maxRange
    this.warnAlertTarget.classList.toggle("hidden", inRange)
  }

  fetchAndFillPositions({ respectExistingPositions = false } = {}) {
    if (!this.hasMfrDateTarget || !this.mfrDateTarget.value || !this.hasSumMscUrlValue) return

    this.fetchController?.abort()
    this.fetchController = new AbortController()

    const url = new URL(this.sumMscUrlValue, window.location.origin)
    url.searchParams.set("msc_date", this.mfrDateTarget.value)
    url.searchParams.set("department_id", this.invoiceOutlet.hasDepartmentSelectTarget ? this.invoiceOutlet.departmentSelectTarget.value : "")
    url.searchParams.set("search_kind", this.hasSearchKindTarget ? this.searchKindTarget.value : "department")

    fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin", signal: this.fetchController.signal })
      .then((response) => response.json())
      .then((result) => {
        if (respectExistingPositions && this.userAddedPosition) return

        if (result.message) showNotification(result.message)
        if (result.focus && this.hasSearchKindTarget) this.searchKindTarget.focus()
        this.fillPositions(result.data || [])
      })
      .catch((error) => {
        if (error.name === "AbortError") return

        showNotification(this.invoiceOutlet.translationsValue.mfr_fetch_positions_error)
        this.clearPositions()
      })
  }

  clearPositions() {
    if (!this.hasInvoicePositionOutlet) return

    this.invoicePositionOutlet.positionsContainer.querySelectorAll("[data-position-row]").forEach((row) => row.remove())
  }

  fillPositions(rows) {
    this.clearPositions()
    if (!this.hasAddPositionButtonTarget) return

    this.autoFilling = true
    rows.forEach((row) => this.fillPositionRow(row))
    this.autoFilling = false
  }

  fillPositionRow(row) {
    const index = this.addPositionRow()
    if (index === null) return

    this.invoicePositionOutlet.fillPosition(index, {
      name: `${this.invoiceOutlet.translationsValue.mfr_position_day} ${row.accounting_income_tax_date}`,
      tax: row.tax,
      totalPriceGross: row.total_price_gross
    })
  }

  addPositionRow() {
    let addedIndex = null
    const captureIndex = (event) => { addedIndex = event.detail.index }

    this.invoicePositionOutlet.element.addEventListener("invoice--position:added", captureIndex, { once: true })
    this.addPositionButtonTarget.click()
    this.invoicePositionOutlet.element.removeEventListener("invoice--position:added", captureIndex)

    return addedIndex
  }
}
